import { useWatch, Controller } from "react-hook-form"
import type { UseFormRegister, FieldErrors, Control, UseFormSetValue, UseFormGetValues } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Input } from "@/components/ui/input"
import { CountrySelect } from "@/components/CountrySelect"
import type { CreateReservationInput } from "@fohet/caravan-booker-shared/types"

interface GuestFormFieldsProps {
    index: number
    register: UseFormRegister<CreateReservationInput>
    errors: FieldErrors<CreateReservationInput>
    control: Control<CreateReservationInput>
    setValue: UseFormSetValue<CreateReservationInput>
    getValues: UseFormGetValues<CreateReservationInput>
}

export const GuestFormFields = ({ index, register, errors, control, setValue, getValues }: GuestFormFieldsProps) => {
    const { t } = useTranslation()
    const guestErrors = errors.guests?.[index]
    const nationality = useWatch({ control, name: `guests.${index}.nationality` })
    const nationalityCode = typeof nationality === "string" ? nationality.toUpperCase() : ""
    // Matches the server-side schema (packages/caravan-booker-shared/src/schemas/reservation.ts).
    // Slovaks are foreign for foreign-police reporting purposes.
    const isForeigner = nationalityCode !== "" && nationalityCode !== "CZ"

    // Snapshot copy address fields from guest 0; user can then edit them freely.
    const copyAddressFromFirstGuest = () => {
        const first = getValues("guests.0")
        if (!first) return
        setValue(`guests.${index}.street`, first.street ?? "", { shouldValidate: true, shouldDirty: true })
        setValue(`guests.${index}.city`, first.city ?? "", { shouldValidate: true, shouldDirty: true })
        setValue(`guests.${index}.zip`, first.zip ?? "", { shouldValidate: true, shouldDirty: true })
        setValue(`guests.${index}.country`, first.country ?? "", { shouldValidate: true, shouldDirty: true })
    }

    return (
        <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <p className="text-xs font-black uppercase tracking-widest text-slate-500">
                {t("components.guestForm.guestLabel", { n: index + 1 })}
            </p>

            {/* Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.firstName")}</label>
                    <Input
                        placeholder="Jan"
                        className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                        error={guestErrors?.firstName?.message}
                        {...register(`guests.${index}.firstName`, { required: t("components.guestForm.required") })}
                    />
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.lastName")}</label>
                    <Input
                        placeholder="Smith"
                        className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                        error={guestErrors?.lastName?.message}
                        {...register(`guests.${index}.lastName`, { required: t("components.guestForm.required") })}
                    />
                </div>
            </div>

            {/* Date of birth + nationality */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.dateOfBirth")}</label>
                    <Input
                        type="date"
                        max={new Date().toISOString().split("T")[0]}
                        className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                        error={guestErrors?.dateOfBirth?.message}
                        {...register(`guests.${index}.dateOfBirth`, {
                            required: t("components.guestForm.required"),
                            validate: (val) => !val || new Date(val) < new Date() || t("components.guestForm.dateOfBirthPast"),
                        })}
                    />
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.nationality")}</label>
                    <Controller
                        control={control}
                        name={`guests.${index}.nationality`}
                        rules={{ required: t("components.guestForm.required") }}
                        render={({ field }) => (
                            <CountrySelect
                                value={typeof field.value === "string" ? field.value : ""}
                                onChange={(code) => field.onChange(code)}
                            />
                        )}
                    />
                    {guestErrors?.nationality?.message && (
                        <p className="text-xs text-red-500 mt-1">{guestErrors.nationality.message}</p>
                    )}
                </div>
            </div>

            {/* Document number */}
            <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">
                    {t("components.guestForm.documentNumber")}
                    {isForeigner && <span className="text-red-500 ml-1">*</span>}
                </label>
                <Input
                    placeholder={t("components.guestForm.documentNumberPlaceholder")}
                    className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                    error={guestErrors?.documentNumber?.message}
                    {...register(`guests.${index}.documentNumber`, {
                        required: isForeigner ? t("components.guestForm.documentNumberRequired") : false,
                        minLength: isForeigner
                            ? { value: 4, message: t("components.guestForm.documentNumberLength") }
                            : undefined,
                        maxLength: isForeigner
                            ? { value: 30, message: t("components.guestForm.documentNumberLength") }
                            : undefined,
                    })}
                />
            </div>

            {/* Address — for guests 2+, offer a quick copy from the first guest */}
            {index > 0 && (
                <div>
                    <button
                        type="button"
                        onClick={copyAddressFromFirstGuest}
                        className="text-xs font-black text-brand hover:text-brand-dark transition-colors underline underline-offset-2"
                    >
                        {t("components.guestForm.copyAddressFromFirstGuest")}
                    </button>
                </div>
            )}

            {/* Permanent address */}
            <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.street")}</label>
                <Input
                    placeholder={t("components.guestForm.streetPlaceholder")}
                    className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                    error={guestErrors?.street?.message}
                    {...register(`guests.${index}.street`, { required: t("components.guestForm.required") })}
                />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="col-span-1">
                    <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.zip")}</label>
                    <Input
                        placeholder={t("components.guestForm.zipPlaceholder")}
                        className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                        error={guestErrors?.zip?.message}
                        {...register(`guests.${index}.zip`, { required: t("components.guestForm.required") })}
                    />
                </div>
                <div className="col-span-1 sm:col-span-2">
                    <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.city")}</label>
                    <Input
                        placeholder={t("components.guestForm.cityPlaceholder")}
                        className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                        error={guestErrors?.city?.message}
                        {...register(`guests.${index}.city`, { required: t("components.guestForm.required") })}
                    />
                </div>
            </div>
            <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">{t("components.guestForm.country")}</label>
                <Input
                    placeholder={t("components.guestForm.countryPlaceholder")}
                    className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                    error={guestErrors?.country?.message}
                    {...register(`guests.${index}.country`, { required: t("components.guestForm.required") })}
                />
            </div>

            {/* Purpose of stay — only for non-CZ nationals */}
            {isForeigner && (
                <div>
                    <label className="text-xs font-bold text-slate-600 mb-1 block">
                        {t("components.guestForm.purposeOfStay")}
                        <span className="text-red-500 ml-1">*</span>
                    </label>
                    <Input
                        placeholder={t("components.guestForm.purposeOfStayPlaceholder")}
                        className="bg-white border border-slate-200 rounded-xl text-sm font-bold"
                        error={guestErrors?.purposeOfStay?.message}
                        {...register(`guests.${index}.purposeOfStay`, {
                            required: isForeigner ? t("components.guestForm.required") : false,
                        })}
                    />
                </div>
            )}
        </div>
    )
}
